import {
  Grid,
  Box,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as passengerSlice from '../../../data/passenger.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as uiSlice from '../../../data/ui.slice';
import { TripLeg } from '../../../data/models/reservation.model';
import reservationUtils from '../../../services/reservationUtils';
import TripCard from '../shared/TripCard';

const PREFIX = 'TripsPage';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  tripFilterContainer: `${PREFIX}-tripFilterContainer`,
  trips: `${PREFIX}-trips`,
  tripRow: `${PREFIX}-trip-row`,
  tripMapContainer: `${PREFIX}-trip-map-container`,
  tripMapSpacer: `${PREFIX}-trip-map-spacer`,
  tripMap: `${PREFIX}-trip-map`,
  tripOuterContainer: `${PREFIX}-trip-outer-container`,
  tripHeaderContainer: `${PREFIX}-trip-header-container`,
  tripContainer: `${PREFIX}-trip-container`,
  tripItem: `${PREFIX}-trip-item`,
  tripItemConnector: `${PREFIX}-trip-item-connector`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    ['& .section-link']: {
      textDecoration: 'none',
      color: 'unset',
    },
  },

  [`&.${classes.header}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    background: `linear-gradient(0deg, ${theme.palette.lightActionDisabled04p.main} 0%, ${theme.palette.lightActionDisabled04p.main}  100%)`,
    padding: theme.spacing(3),
    [`& h1`]: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(1),
    },
  },

  [`&.${classes.tripFilterContainer}`]: {
    maxWidth: '394px',
    marginBottom: theme.spacing(2),
  },

  [`&.${classes.trips}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  [`&.${classes.tripRow}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },

  [`&.${classes.tripMapContainer}`]: {
    flex: 1,
    display: 'flex',
  },

  [`&.${classes.tripMapSpacer}`]: {
    flex: 1,
    minWidth: 20,
    [theme.breakpoints.up('md')]: {
      minWidth: 50,
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: 75,
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: 100,
    },
  },

  [`&.${classes.tripMap}`]: {
    width: '100%',
    minWidth: 170,
    maxWidth: 483,
  },

  [`&.${classes.tripOuterContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },

  [`&.${classes.tripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },

  [`&.${classes.tripHeaderContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },

  [`&.${classes.tripItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    [`& svg`]: {
      backgroundColor: theme.palette.background.default,
    },
  },

  [`&.${classes.tripItemConnector}`]: {
    width: theme.spacing(0.5),
    height: '100%',
    margin: '0 auto',
    marginTop: theme.spacing(-0.625),
    backgroundImage: "url('/dot-small.svg')",
  },
}));

export enum TripType {
  All = '1',
  Upcoming = '2',
  Past = '3',
}

const TripsPage = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const tripFilterButtons = [
    { label: t('member.all'), value: TripType.All },
    { label: t('member.upcoming'), value: TripType.Upcoming },
    { label: t('member.past'), value: TripType.Past },
  ];

  const dispatch = useAppDispatch();
  const selectedFilter = useAppSelector(uiSlice.selectTripsPageFilterOption);
  const passenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const futureReservations = useAppSelector(
    passengerSlice.selectCurrentPassengerFutureReservations,
  );
  const pastReservations = useAppSelector(
    passengerSlice.selectCurrentPassengerPastReservations,
  );
  const tripLegDetails = useAppSelector(tripLegSlice.selectTripLegDetails);

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: TripType,
  ) => {
    dispatch(uiSlice.setTripsPageFilterOption(newFilter));
  };

  useEffect(() => {
    if (!passenger) return;
    if (passenger.passengerUUID) {
      dispatch(
        passengerSlice.fetchPassengerFutureReservationsAsync(
          passenger.passengerUUID,
          100,
        ),
      );
      dispatch(
        passengerSlice.fetchPassengerPastReservationsAsync(
          passenger.passengerUUID,
          30, // last 30 trips only
        ),
      );
    }
  }, [dispatch, passenger]);

  useEffect(() => {
    if (pastReservations && pastReservations.reservations) {
      const legIds = pastReservations?.reservations
        .flatMap((res) => res.tripLegs || [])
        .map((leg) => leg.tripLegReferenceID) as string[];
      legIds.forEach((id) => {
        dispatch(tripLegSlice.getTripLegDetailsAsync(id));
      });
    }
  }, [dispatch, futureReservations, pastReservations]);

  const retrieveProviderName = (leg: TripLeg) => {
    return leg.tripLegReferenceID
      ? (tripLegDetails[leg.tripLegReferenceID]?.transportationProvider
          ?.transportationProviderName ?? '--')
      : '--';
  };

  const retrieveDriverName = (leg: TripLeg) => {
    return leg.tripLegReferenceID
      ? reservationUtils.getDriverName(
          tripLegDetails[leg.tripLegReferenceID]?.driver,
        )
      : '--';
  };

  return (
    <>
      <StyledBox className={classes.header + ' standout-section standout-top'}>
        <Box className="standout-section-content">
          <Typography variant="h1">{t('member.trips')}</Typography>
          <StyledBox className={classes.tripFilterContainer}>
            <ToggleButtonGroup
              exclusive
              value={selectedFilter}
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  handleFilterChange(event, newValue);
                }
              }}>
              {tripFilterButtons.map((button) => (
                <ToggleButton
                  key={'tftb-' + button.value}
                  value={button.value}
                  className="white-background">
                  {button.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </StyledBox>
        </Box>
      </StyledBox>
      <StyledBox className={classes.container}>
        <Grid container>
          <Grid item xs={12} md={12} lg={8} xl={9}>
            <StyledBox className={classes.container}>
              {selectedFilter === TripType.All ||
              selectedFilter === TripType.Upcoming ? (
                <>
                  {futureReservations.reservations.length > 0 && (
                    <Typography variant="h3">
                      {t('member.upcomingTrips')}
                    </Typography>
                  )}
                  {[...futureReservations.reservations]
                    ?.sort((a, b) => {
                      const aDate = a?.reservationServiceDate ?? '';
                      const bDate = b?.reservationServiceDate ?? '';
                      return aDate.localeCompare(bDate) || 0;
                    })
                    ?.map(
                      (reservation) =>
                        reservation.tripLegs &&
                        reservation.tripLegs.map((_leg, legIndex) => {
                          return (
                            <TripCard
                              key={
                                reservation.tripLegs[legIndex]
                                  .tripLegReferenceID
                              }
                              reservation={reservation}
                              legIndex={legIndex}
                              enableTracking={true}
                              fromTripsPage={true}
                            />
                          );
                        }),
                    )}
                </>
              ) : null}

              {selectedFilter === TripType.All ||
              selectedFilter === TripType.Past ? (
                <>
                  {pastReservations.reservations.length > 0 && (
                    <Typography variant="h3">
                      {t('member.pastTrips')}
                    </Typography>
                  )}
                  {[...pastReservations.reservations]
                    ?.sort((a, b) => {
                      const aDate = a?.reservationServiceDate ?? '';
                      const bDate = b?.reservationServiceDate ?? '';
                      return aDate.localeCompare(bDate) || 0;
                    })
                    ?.map(
                      (reservation) =>
                        reservation.tripLegs &&
                        reservation.tripLegs.map((_leg, legIndex) => {
                          return (
                            <TripCard
                              key={
                                reservation.tripLegs[legIndex]
                                  .tripLegReferenceID
                              }
                              reservation={reservation}
                              legIndex={legIndex}
                              enableTracking={true}
                              fromTripsPage={true}
                            />
                          );
                        }),
                    )}
                </>
              ) : null}
            </StyledBox>
          </Grid>
        </Grid>
      </StyledBox>
    </>
  );
};

export default TripsPage;
